import React from "react";
import {
  BookOpen, Clock, Landmark, Users, Lightbulb,
  CheckCircle2, Target, PenTool, Sparkles,
} from "lucide-react";

const renderInline = (text) => {
  if (typeof text !== "string") return text;
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
};

const Para = ({ children, className = "" }) =>
  children ? (
    <p className={`text-base md:text-lg leading-loose text-slate-700 ${className}`}>{renderInline(children)}</p>
  ) : null;

const SectionHeading = ({ num, icon: Icon, children }) => (
  <h4 className="text-lg md:text-xl font-semibold tracking-tight text-slate-800 font-serif mt-8 mb-3 flex items-center gap-2">
    {num && <span className="font-mono text-sm text-slate-400">{num}</span>}
    {Icon && <Icon className="w-5 h-5 text-slate-500" />}
    <span>{children}</span>
  </h4>
);

const toList = (v) => (Array.isArray(v) ? v.filter(Boolean) : v ? [v] : []);

export const InDepthArticle = ({ article: a }) => {
  if (!a) return null;
  const gs = a.syllabus?.gs_paper || a.gs_paper || "GS";
  const mp = a.multiple_perspectives || {};
  const rel = a.upsc_relevance || {};
  const prelims = a.probable_prelims || a.prelims_mcq;
  const mains = [a.probable_mains_10m, a.probable_mains_15m].filter(Boolean);
  const wayForwardPoints = toList(a.way_forward).length > 1 ? toList(a.way_forward) : null;

  return (
    <article
      data-testid="indepth-article"
      className="max-w-4xl mx-auto bg-white rounded-sm border border-slate-200 shadow-sm overflow-hidden"
    >
      <div className="p-6 md:p-12">
        {/* Meta header */}
        <div className="flex flex-wrap items-center gap-3 mb-8 pb-6 border-b border-slate-100">
          <span
            data-testid="article-gs-badge"
            className="border border-slate-300 px-2 py-1 rounded-sm text-xs font-mono font-semibold text-slate-800 tracking-wider"
          >
            {gs}
          </span>
          {a.is_thematic && (
            <span
              data-testid="article-evergreen-badge"
              className="flex items-center gap-1 border border-amber-200 bg-amber-50 px-2 py-1 rounded-sm text-xs font-medium text-amber-900"
            >
              <Sparkles className="w-3 h-3" /> Evergreen Concept
            </span>
          )}
          <span data-testid="article-reading-time" className="flex items-center gap-1 text-xs text-slate-500 ml-auto">
            <Clock className="w-3.5 h-3.5" /> {a.reading_time || 3} min read
          </span>
        </div>

        {/* Title */}
        <h3
          data-testid="article-title"
          className="text-2xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight font-serif mb-10"
        >
          {a.title}
        </h3>

        {/* Answer body — left "answer margin" motif */}
        <div className="ml-1 md:ml-4 pl-4 md:pl-8 border-l-[3px] border-double border-slate-200">
          {a.introduction && (
            <div data-testid="section-introduction">
              <SectionHeading num="I." icon={BookOpen}>Introduction</SectionHeading>
              <Para>{a.introduction}</Para>
            </div>
          )}
          {a.background && (
            <div data-testid="section-background">
              <SectionHeading num="II." >Background</SectionHeading>
              <Para>{a.background}</Para>
            </div>
          )}
          {a.current_development && (
            <div data-testid="section-current-development">
              <SectionHeading num="III.">Current Development</SectionHeading>
              <Para>{a.current_development}</Para>
            </div>
          )}
        </div>

        {/* Perspectives */}
        {(mp.government_view || mp.opposition_view || mp.expert_opinion) && (
          <>
            <SectionHeading num="IV." icon={Users}>Multiple Perspectives</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-6">
              <div data-testid="perspective-gov" className="p-5 border border-slate-200 bg-slate-50/60 rounded-sm">
                <div className="flex items-center gap-2 mb-2 text-slate-800">
                  <Landmark className="w-4 h-4" />
                  <p className="text-xs font-semibold tracking-[0.1em] uppercase font-mono">Government</p>
                </div>
                <p className="text-sm leading-relaxed text-slate-600">{renderInline(mp.government_view)}</p>
              </div>
              <div data-testid="perspective-opp" className="p-5 border border-slate-200 bg-slate-50/60 rounded-sm">
                <div className="flex items-center gap-2 mb-2 text-slate-800">
                  <Users className="w-4 h-4" />
                  <p className="text-xs font-semibold tracking-[0.1em] uppercase font-mono">Opposition</p>
                </div>
                <p className="text-sm leading-relaxed text-slate-600">{renderInline(mp.opposition_view)}</p>
              </div>
              <div data-testid="perspective-expert" className="p-5 border border-slate-200 bg-slate-50/60 rounded-sm">
                <div className="flex items-center gap-2 mb-2 text-slate-800">
                  <Lightbulb className="w-4 h-4" />
                  <p className="text-xs font-semibold tracking-[0.1em] uppercase font-mono">Expert View</p>
                </div>
                <p className="text-sm leading-relaxed text-slate-600">{renderInline(mp.expert_opinion)}</p>
              </div>
            </div>
          </>
        )}

        {/* Way Forward & Conclusion */}
        <div className="ml-1 md:ml-4 pl-4 md:pl-8 border-l-[3px] border-double border-slate-200">
          {a.way_forward && (
            <div data-testid="section-way-forward">
              <SectionHeading num="V." icon={CheckCircle2}>Way Forward</SectionHeading>
              {wayForwardPoints ? (
                <ul className="list-disc pl-5 space-y-2 text-slate-700 marker:text-slate-400">
                  {wayForwardPoints.map((p, i) => <li key={i} className="leading-relaxed">{renderInline(p)}</li>)}
                </ul>
              ) : (
                <Para>{a.way_forward}</Para>
              )}
            </div>
          )}
          {a.conclusion && (
            <div data-testid="section-conclusion">
              <SectionHeading num="VI." icon={Target}>Conclusion</SectionHeading>
              <Para className="italic text-slate-700">{a.conclusion}</Para>
            </div>
          )}
        </div>

        {/* UPSC Relevance — value-addition note card */}
        {(toList(rel.prelims_facts).length > 0 || toList(rel.mains_arguments).length > 0) && (
          <div
            data-testid="upsc-relevance-box"
            className="my-12 p-6 md:p-8 bg-[#FDFBF7] border border-[#E5E2DC] shadow-[4px_4px_0px_rgba(0,0,0,0.05)]"
          >
            <div className="flex items-center gap-2 text-slate-900 font-serif font-bold text-lg md:text-xl mb-6 border-b border-slate-200 pb-4">
              <BookOpen className="w-5 h-5" /> Why It Matters for UPSC
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {toList(rel.prelims_facts).length > 0 && (
                <div>
                  <p className="text-xs font-semibold tracking-[0.1em] uppercase font-mono text-slate-500 mb-3">Prelims Facts</p>
                  <ul data-testid="prelims-facts-list" className="list-disc pl-5 space-y-2.5 text-sm text-slate-700 marker:text-slate-400">
                    {toList(rel.prelims_facts).map((f, i) => <li key={i} className="leading-relaxed">{renderInline(f)}</li>)}
                  </ul>
                </div>
              )}
              {toList(rel.mains_arguments).length > 0 && (
                <div>
                  <p className="text-xs font-semibold tracking-[0.1em] uppercase font-mono text-slate-500 mb-3">Mains Value-Addition</p>
                  <ul data-testid="mains-arguments-list" className="list-disc pl-5 space-y-2.5 text-sm text-slate-700 marker:text-slate-400">
                    {toList(rel.mains_arguments).map((m, i) => <li key={i} className="leading-relaxed">{renderInline(m)}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Probable Questions — exam-mode block */}
        {(prelims || mains.length > 0) && (
          <div data-testid="probable-questions-block" className="mt-12 bg-white border-2 border-slate-900 p-6 md:p-8">
            <div className="text-xl md:text-2xl font-serif font-bold text-slate-900 mb-8 flex items-center gap-2">
              <PenTool className="w-5 h-5" /> Probable Questions
            </div>

            {prelims && (
              <div className="mb-8 pb-8 border-b border-slate-200">
                <p className="text-xs font-semibold tracking-[0.1em] uppercase font-mono text-slate-500 mb-3">Prelims (MCQ)</p>
                <p data-testid="probable-prelims-question" className="font-medium text-slate-900 mb-4">{renderInline(prelims.question)}</p>
                <div data-testid="mcq-options" className="grid grid-cols-1 gap-3 mb-4">
                  {toList(prelims.options).map((opt, i) => {
                    const correct = i === prelims.correct_answer;
                    return (
                      <div
                        key={i}
                        className={`flex items-start gap-3 p-3 border rounded-sm text-sm transition-colors ${
                          correct ? "border-emerald-500 bg-emerald-50/60" : "border-slate-200"
                        }`}
                      >
                        <span className="font-mono font-semibold text-slate-500">{String.fromCharCode(65 + i)}.</span>
                        <span className="text-slate-700">{renderInline(opt)}</span>
                        {correct && <CheckCircle2 className="w-4 h-4 text-emerald-600 ml-auto shrink-0" />}
                      </div>
                    );
                  })}
                </div>
                {prelims.explanation && (
                  <div data-testid="mcq-explanation" className="bg-slate-50 p-4 border-l-4 border-slate-800 text-sm text-slate-700 leading-relaxed">
                    <span className="font-semibold text-slate-900">Explanation: </span>{renderInline(prelims.explanation)}
                  </div>
                )}
              </div>
            )}

            {mains.length > 0 && (
              <div className="space-y-8">
                <p className="text-xs font-semibold tracking-[0.1em] uppercase font-mono text-slate-500">Mains</p>
                {mains.map((q, i) => (
                  <div key={i} data-testid={i === 0 ? "probable-mains-10m" : "probable-mains-15m"} className="relative pl-6 border-l-2 border-slate-900">
                    <span className="absolute -left-[1px] -top-3 bg-white px-2 text-xs font-mono font-bold text-slate-500">
                      {q.marks || (i === 0 ? 10 : 15)} marks
                    </span>
                    <p className="font-medium text-slate-900 mb-3">{renderInline(q.question)}</p>
                    {toList(q.approach_hints).length > 0 && (
                      <div data-testid="mains-approach-hints" className="mt-2 text-sm text-slate-600 bg-slate-50 p-4 rounded-sm">
                        <p className="font-semibold text-slate-700 mb-2">Approach</p>
                        <ul className="list-disc pl-5 space-y-1.5 marker:text-slate-400">
                          {toList(q.approach_hints).map((h, j) => <li key={j}>{renderInline(h)}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default InDepthArticle;
